import * as React from 'react';
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useCategories } from '../../../application/hooks/useCategories';
import * as types from '../../../application/_types/types.type';
import { PageHeader } from '../../components/page-header';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';

// Lazy-load the existing MUI tab components for now.
// These will be individually ported to v2 as we go.
const PinsConfig = React.lazy(
  () =>
    import(
      '../../../application/routes/settings/components/Configuration/PinsConfig.view'
    )
);
const StageConfig = React.lazy(
  () =>
    import(
      '../../../application/routes/settings/components/Configuration/StageConfig.view'
    )
);
const FormConfig = React.lazy(
  () =>
    import(
      '../../../application/routes/settings/components/Configuration/FormConfig.view'
    )
);
const FeaturesConfig = React.lazy(
  () =>
    import(
      '../../../application/routes/settings/components/Configuration/FeaturesConfig.view'
    )
);
const MessagesConfig = React.lazy(
  () =>
    import(
      '../../../application/routes/settings/components/Configuration/MessagesConfig.view'
    )
);

declare const global: {
  me: {
    id: string;
    imageName?: string;
    name: string;
  };
};

export function SalesToolsPage() {
  const { categories } = useCategories();
  const [selectedCRM, setSelectedCRM] =
    useState<types.StageCategoryPartial | null>(null);
  const [activeTab, setActiveTab] = useState('pins');

  useEffect(() => {
    if (categories.length > 0) {
      const defaultCategory = categories.find((c) => c.isDefault);
      setSelectedCRM(
        (defaultCategory || categories[0]) as types.StageCategoryPartial
      );
    }
  }, [categories]);

  const tabTriggerClass =
    'rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary px-4 pb-3 pt-1 text-sm whitespace-nowrap text-muted-foreground';

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Sales Tools"
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <div>
            <h2 className="text-xl font-bold text-foreground">
              Sales Tools Customization
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Customize pins, deals, and forms to align with your unique sales
              process.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-0 overflow-x-auto">
              <TabsTrigger value="pins" className={tabTriggerClass}>
                Pins
              </TabsTrigger>
              <TabsTrigger value="stages" className={tabTriggerClass}>
                Stages
              </TabsTrigger>
              <TabsTrigger value="form" className={tabTriggerClass}>
                Form
              </TabsTrigger>
              <TabsTrigger value="features" className={tabTriggerClass}>
                Features
              </TabsTrigger>
              <TabsTrigger value="messages" className={tabTriggerClass}>
                Messages
              </TabsTrigger>
            </TabsList>

            <React.Suspense
              fallback={
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <TabsContent value="pins" className="mt-4">
                <PinsConfig selectedCRM={selectedCRM} />
              </TabsContent>
              <TabsContent value="stages" className="mt-4">
                <StageConfig selectedCRM={selectedCRM} />
              </TabsContent>
              <TabsContent value="form" className="mt-4">
                <FormConfig selectedCRM={selectedCRM} />
              </TabsContent>
              <TabsContent value="features" className="mt-4">
                <FeaturesConfig
                  selectedCRM={selectedCRM}
                  categories={categories as types.StageCategoryPartial[]}
                />
              </TabsContent>
              <TabsContent value="messages" className="mt-4">
                <MessagesConfig />
              </TabsContent>
            </React.Suspense>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
